import { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { Textarea, Select, NumberInput, Text, Paper } from "@mantine/core";
import { getTicketData, updateTicketData } from "../../services/ticket.js";
import "../../components/PrintTicket/PrintTicket.scss";
import "./Ticket.scss";
import { usePermissions } from "../../contexts/PermissionsContext";

const useDebounce = (callback, delay) => {
  const [timeoutId, setTimeoutId] = useState(null);

  return useCallback(
    (...args) => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      const newTimeoutId = setTimeout(() => {
        callback(...args);
      }, delay);

      setTimeoutId(newTimeoutId);
    },
    [callback, delay, timeoutId]
  );
};

const Ticket = () => {
  const [message, setMessage] = useState("");
  const [fontFamily, setFontFamily] = useState("Arial");
  const [fontSize, setFontSize] = useState(14);
  const [fontWeight, setFontWeight] = useState("normal");
  const userData = useSelector((state) => state.user.user);
  const { hasPermission } = usePermissions();
  const canEdit = hasPermission({ feature: "ticket", action: "Editar" });

  // Font options
  const fontFamilyOptions = [
    { value: "Arial", label: "Arial" },
    { value: "Times New Roman", label: "Times New Roman" },
    { value: "Courier New", label: "Courier New" },
    { value: "ArialPixel", label: "ArialPixel" },
  ];

  const fontWeightOptions = [
    { value: "normal", label: "Normal" },
    { value: "bold", label: "Gruesa" },
    { value: "300", label: "Ligera" },
    { value: "600", label: "Semi Gruesa" },
  ];

  useEffect(() => {
    const fetchTicketData = async () => {
      if (userData && userData._id) {
        const ticketData = await getTicketData(userData._id);
        if (ticketData) {
          setMessage(ticketData.text || "");
          setFontFamily(ticketData.fontFamily || "Arial");
          setFontSize(ticketData.fontSize || 14);
          setFontWeight(ticketData.fontWeight || "normal");
        }
      }
    };

    fetchTicketData();
  }, [userData]);

  const saveChanges = async (updatedData) => {
    if (userData && userData._id) {
      await updateTicketData({
        userId: userData._id,
        text: updatedData.text !== undefined ? updatedData.text : message,
        fontFamily:
          updatedData.fontFamily !== undefined
            ? updatedData.fontFamily
            : fontFamily,
        fontSize:
          updatedData.fontSize !== undefined ? updatedData.fontSize : fontSize,
        fontWeight:
          updatedData.fontWeight !== undefined
            ? updatedData.fontWeight
            : fontWeight,
      });
    }
  };

  const debouncedSave = useDebounce(saveChanges, 1000);

  const handleMessageChange = (e) => {
    const newValue = e.currentTarget.value;
    setMessage(newValue);
    if (canEdit) debouncedSave({ text: newValue });
  };

  const handleFontFamilyChange = (value) => {
    setFontFamily(value);
    if (canEdit) debouncedSave({ fontFamily: value });
  };

  const handleFontSizeChange = (value) => {
    setFontSize(value);
    if (canEdit) debouncedSave({ fontSize: value });
  };

  const handleFontWeightChange = (value) => {
    setFontWeight(value);
    if (canEdit) debouncedSave({ fontWeight: value });
  };

  // Calculate dynamic styles for the ticket preview
  const ticketTextStyle = {
    fontFamily: fontFamily,
    fontSize: fontSize,
    fontWeight: fontWeight,
  };

  // Font style object for the TicketContent component
  const ticketFontStyle = {
    fontFamily: fontFamily,
    fontSize: fontSize,
    fontWeight: fontWeight,
  };

  // Dummy products data for the preview
  const currentProducts = [
    { id: 1, name: "Producto 1", quantity: 2, price: 100 },
    { id: 2, name: "Producto 2", quantity: 1, price: 200 },
    { id: 3, name: "Producto 3", quantity: 1, price: 70 },
  ];
  const total = currentProducts.reduce(
    (acc, item) => acc + item.price * item.quantity,
    0
  );
  const payment = 500; // Example payment amount

  return (
    <div className="ticket-page">
      <h1>Ticket</h1>
      <p style={{ textAlign: "left" }}>
        Agrega un mensaje personalizado al final de los tickets de compra que
        emites y personaliza su apariencia
      </p>

      <br />

      <div className="ticket-layout">
        <div className="ticket-left">
          <Paper shadow="sm" p="md" radius="md">
            <Text size="lg" weight={500} mb="md">
              Personalización de Fuente
            </Text>

            <Select
              label="Tipo de Fuente"
              placeholder="Selecciona una fuente"
              data={fontFamilyOptions}
              value={fontFamily}
              onChange={handleFontFamilyChange}
              disabled={!canEdit}
              mb="md"
            />

            <NumberInput
              label="Tamaño de Fuente"
              placeholder="14"
              value={fontSize}
              onChange={handleFontSizeChange}
              min={5}
              max={20}
              disabled={!canEdit}
              mb="md"
            />

            <Select
              label="Peso de Fuente"
              placeholder="Selecciona el peso"
              data={fontWeightOptions}
              value={fontWeight}
              onChange={handleFontWeightChange}
              disabled={!canEdit}
              mb="md"
            />

            <Text size="sm" color="dimmed">
              Los cambios se guardan automáticamente y se aplicarán a todos los
              tickets.
            </Text>
          </Paper>
        </div>

        <div className="ticket-right">
          <div
            className="print-ticket reveal"
            style={{
              fontSize: ticketTextStyle.fontSize + "px",
              fontWeight: ticketTextStyle.fontWeight,
              fontFamily: ticketTextStyle.fontFamily,
              borderRadius: "5px",
              border: "solid 1px grey",
              padding: "20px",
            }}
          >
            <h1>Tu Empresa</h1>
            <b>Ticket de compra</b>
            <br />
            {new Date().toLocaleString()}
            <br />
            <Textarea
              placeholder="Escribe tu mensaje aquí"
              value={message}
              onChange={handleMessageChange}
              rows={4}
              disabled={!canEdit}
              autosize
            />
            <div className="breaker"></div>
            <div className="print-ticket__content">
              <table className="ticket-table" style={{ boxShadow: "none" }}>
                <thead>
                  <tr className="ticket-item">
                    <th style={{ background: "transparent", color: "#333" }}>
                      Producto
                    </th>
                    <th style={{ background: "transparent", color: "#333" }}>
                      P/U
                    </th>
                    <th style={{ background: "transparent", color: "#333" }}>
                      Total
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {currentProducts.map((item) => (
                    <tr key={item.id} className="ticket-item">
                      <td>
                        {item.name} x{item.quantity}
                      </td>
                      <td>${item.price.toFixed(2)}</td>
                      <td>${(item.price * item.quantity).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="breaker"></div>
            <div className="ticket-item">
              <div>
                <h3>Total</h3>
              </div>
              <div>${Number(total).toFixed(2)}</div>
            </div>
            {payment > 0 && (
              <>
                <div className="ticket-item">
                  <div>
                    <h3>Pago</h3>
                  </div>
                  <div>${Number(payment).toFixed(2)}</div>
                </div>
                <div className="ticket-item">
                  <div>
                    <h3>Cambio</h3>
                  </div>
                  <div>${Number(payment - total).toFixed(2)}</div>
                </div>
              </>
            )}
            <div className="breaker"></div>
            <h2>¡Gracias por su compra!</h2>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Ticket;
